import React, { useEffect, useState } from "react";
import {
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";

import {
  FiAlertCircle,
  FiArrowRight,
  FiCheckCircle,
  FiLoader,
  FiLock,
  FiMail,
  FiUserPlus,
  FiLogOut,
  FiRefreshCw,
} from "react-icons/fi";

import SafeIcon from "../common/SafeIcon";
import supabase from "../supabase/supabase";
import { useClient } from "../context/ClientContext";

export const INVITATION_STORAGE_KEY =
  "profityo_pending_client_invitation";

const validToken = (value) =>
  typeof value === "string" &&
  /^[0-9a-f]{64}$/i.test(value);

const readPendingToken = () => {
  try {
    const value = sessionStorage.getItem(
      INVITATION_STORAGE_KEY
    );

    return validToken(value) ? value : "";
  } catch {
    return "";
  }
};

const savePendingToken = (token) => {
  if (!validToken(token)) return;

  try {
    sessionStorage.setItem(
      INVITATION_STORAGE_KEY,
      token
    );
  } catch {
    // Storage can be unavailable.
  }
};

const clearPendingToken = () => {
  try {
    sessionStorage.removeItem(
      INVITATION_STORAGE_KEY
    );
  } catch {
    // Storage can be unavailable.
  }
};

function LoadingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center gap-3 bg-slate-50 text-sm text-slate-600">
      <SafeIcon
        icon={FiLoader}
        className="animate-spin"
      />
      Checking your session...
    </div>
  );
}

export default function AcceptInvitation() {
  const {
    isAuthenticated,
    isLoading,
    user,
    refreshProfile,
    logout,
  } = useClient();

  const location = useLocation();
  const navigate = useNavigate();

  const urlToken =
    new URLSearchParams(location.search).get(
      "token"
    ) || "";

  /*
   * Prefer the explicit invitation URL.
   *
   * Otherwise recover an invitation saved
   * during login or client registration.
   */

  const token = validToken(urlToken)
    ? urlToken
    : urlToken
      ? ""
      : readPendingToken();

  const [submitting, setSubmitting] =
    useState(false);

  const [switchingAccount, setSwitchingAccount] =
    useState(false);

  const [refreshing, setRefreshing] =
    useState(false);

  const [accepted, setAccepted] =
    useState(null);

  const [workspaceReady, setWorkspaceReady] =
    useState(false);

  const [error, setError] =
    useState("");

  const [workspaceMessage, setWorkspaceMessage] =
    useState("");

  /*
   * Preserve the incoming invitation.
   */

  useEffect(() => {
    if (!accepted && validToken(token)) {
      savePendingToken(token);
    }
  }, [token, accepted]);

  /*
   * Restore the token to the URL when
   * returning from login without a query.
   *
   * HashRouter navigation preserves the
   * application's route format.
   */

  useEffect(() => {
    if (
      !accepted &&
      !urlToken &&
      validToken(token)
    ) {
      navigate(
        `/accept-invitation?token=${encodeURIComponent(
          token
        )}`,
        { replace: true }
      );
    }
  }, [
    accepted,
    urlToken,
    token,
    navigate,
  ]);

  /*
   * Signup and sign-in do not grant
   * membership directly.
   *
   * The database acceptance function is
   * responsible for authorization.
   */

  const openSignIn = () => {
    if (!validToken(token)) return;

    savePendingToken(token);
    navigate("/login");
  };

  const openRegistration = () => {
    if (!validToken(token)) return;

    savePendingToken(token);
    navigate("/client-register");
  };

  /*
   * Re-read the authenticated organization's
   * memberships after acceptance.
   */

  const reloadWorkspace = async (
    expectedOrganizationId
  ) => {
    setRefreshing(true);
    setWorkspaceMessage("");

    try {
      const result = await refreshProfile();

      if (!result?.success) {
        setWorkspaceReady(false);

        setWorkspaceMessage(
          result?.error ||
            "Your invitation was accepted, but your workspace could not be loaded yet. Please retry."
        );

        return false;
      }

      const matchingClient =
        result.data?.clients?.find(
          (client) =>
            client.id ===
            expectedOrganizationId
        );

      if (
        expectedOrganizationId &&
        !matchingClient
      ) {
        setWorkspaceReady(false);

        setWorkspaceMessage(
          "Your invitation was accepted, but your organization is not appearing in your accessible workspaces yet. Please retry after checking your organization membership."
        );

        return false;
      }

      setWorkspaceReady(true);
      setWorkspaceMessage("");

      return true;
    } catch (refreshError) {
      console.error(
        "Unable to refresh client workspace:",
        refreshError
      );

      setWorkspaceReady(false);

      setWorkspaceMessage(
        "Your invitation was accepted, but your workspace could not be refreshed. Please retry."
      );

      return false;
    } finally {
      setRefreshing(false);
    }
  };

  /*
   * Accept invitation once.
   *
   * IMPORTANT:
   * A successful acceptance is not reverted
   * just because workspace refresh fails.
   */

  const accept = async () => {
    if (
      !validToken(token) ||
      !isAuthenticated ||
      submitting ||
      accepted
    ) {
      return;
    }

    setSubmitting(true);
    setError("");
    setWorkspaceMessage("");

    try {
      const {
        data,
        error: functionError,
      } = await supabase.functions.invoke(
        "accept-client-invitation",
        {
          body: { token },
        }
      );

      if (functionError) {
        let message =
          functionError.message ||
          "Unable to accept the invitation.";

        try {
          const response =
            await functionError.context?.json?.();

          if (
            typeof response?.error === "string"
          ) {
            message = response.error;
          }
        } catch {
          // Network or non-JSON error.
        }

        throw new Error(message);
      }

      if (!data?.success) {
        throw new Error(
          data?.error ||
            "Invitation acceptance was unsuccessful."
        );
      }

      /*
       * Membership was created successfully.
       * Remove the pending invitation now,
       * not after refreshing workspace state.
       */

      setAccepted(data);
      clearPendingToken();

      await reloadWorkspace(
        data.client_organization_id
      );
    } catch (requestError) {
      setError(
        requestError?.message ||
          "Unable to accept this invitation."
      );
    } finally {
      setSubmitting(false);
    }
  };

  /*
   * Allow users to change accounts without
   * losing the invitation.
   */

  const switchAccount = async () => {
    if (
      !validToken(token) ||
      switchingAccount
    ) {
      return;
    }

    setSwitchingAccount(true);
    setError("");

    savePendingToken(token);

    try {
      const result = await logout();

      if (!result?.success) {
        throw new Error(
          result?.error ||
            "Unable to sign out."
        );
      }

      navigate("/login", {
        replace: true,
      });
    } catch (logoutError) {
      setError(
        logoutError?.message ||
          "Unable to switch accounts."
      );
    } finally {
      setSwitchingAccount(false);
    }
  };

  const continueToWorkspace = () => {
    if (!workspaceReady) return;

    /*
     * The application's normal landing
     * route is the AI workspace.
     *
     * Organization membership and RLS
     * determine actual data access.
     *
     * Replace "/" with a dedicated client
     * portal route when that route exists.
     */

    navigate("/client", { replace: true });
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  const invalid = !validToken(token);

  const verified = Boolean(
    user?.email_confirmed_at ||
    user?.confirmed_at
  );

  return (
    <div className="flex min-h-[100dvh] w-full items-start justify-center overflow-x-hidden bg-slate-50 px-4 py-10 font-sans sm:items-center">
      <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-7 shadow-sm sm:p-9">

        {/* ICON */}

        <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700">
          <SafeIcon
            icon={
              accepted
                ? FiCheckCircle
                : FiMail
            }
            className="text-2xl"
          />
        </div>

        {/* HEADING */}

        <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">
          ProfitYo · Client Portal
        </p>

        <h1 className="mt-2 text-3xl font-black tracking-tight text-slate-900">
          {accepted
            ? "Invitation accepted"
            : "Accept your invitation"}
        </h1>

        <p className="mt-3 text-sm leading-7 text-slate-600">
          {accepted
            ? "Your membership has been created. We are preparing access to your client organization."
            : "You've been invited to join a client organization on ProfitYo. Sign in or create a client account using the email address that received the invitation."}
        </p>

        {/* ERROR */}

        {error && (
          <div
            role="alert"
            className="mt-5 flex items-start gap-3 rounded-2xl border border-red-100 bg-red-50 p-4 text-sm text-red-700"
          >
            <SafeIcon
              icon={FiAlertCircle}
              className="mt-0.5 shrink-0"
            />
            <span>{error}</span>
          </div>
        )}

        {/* ACCEPTED */}

        {accepted ? (
          <div className="mt-6 space-y-4">
            <div className="flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
              <SafeIcon
                icon={FiCheckCircle}
                className="mt-0.5 shrink-0 text-emerald-700"
              />

              <div>
                <p className="text-sm font-bold text-emerald-900">
                  Membership created
                </p>

                <p className="mt-1 text-xs leading-6 text-emerald-800">
                  Your invitation has been accepted
                  successfully.
                </p>
              </div>
            </div>

            {workspaceMessage && (
              <div
                role="status"
                className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-800"
              >
                {workspaceMessage}
              </div>
            )}

            {workspaceReady ? (
              <button
                type="button"
                onClick={continueToWorkspace}
                className="flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-5 py-4 text-sm font-bold text-white hover:bg-emerald-700"
              >
                Continue to workspace

                <SafeIcon
                  icon={FiArrowRight}
                />
              </button>
            ) : (
              <button
                type="button"
                onClick={() =>
                  reloadWorkspace(
                    accepted.client_organization_id
                  )
                }
                disabled={refreshing}
                className="flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 px-5 py-4 text-sm font-bold text-white hover:bg-black disabled:opacity-60"
              >
                <SafeIcon
                  icon={
                    refreshing
                      ? FiLoader
                      : FiRefreshCw
                  }
                  className={
                    refreshing
                      ? "animate-spin"
                      : ""
                  }
                />

                {refreshing
                  ? "Loading workspace..."
                  : "Retry workspace loading"}
              </button>
            )}
          </div>
        ) : invalid ? (

          /* INVALID INVITATION */

          <div
            role="alert"
            className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-800"
          >
            This invitation link is missing or
            invalid. Please request a new
            invitation from your accounting firm.
          </div>

        ) : !isAuthenticated ? (

          /* NEW OR EXISTING CLIENT */

          <div className="mt-7 space-y-3">

            <button
              type="button"
              onClick={openRegistration}
              className="flex w-full items-center justify-center gap-3 rounded-2xl bg-emerald-600 px-5 py-4 text-sm font-bold text-white transition-colors hover:bg-emerald-700"
            >
              <SafeIcon icon={FiUserPlus} />
              Create Client Account
              <SafeIcon icon={FiArrowRight} />
            </button>

            <button
              type="button"
              onClick={openSignIn}
              className="flex w-full items-center justify-center gap-3 rounded-2xl border-2 border-slate-200 bg-white px-5 py-4 text-sm font-bold text-slate-800 transition-colors hover:bg-slate-50"
            >
              I Already Have an Account
            </button>

            <p className="pt-2 text-center text-xs leading-5 text-slate-500">
              Your invitation will remain saved
              in this browser tab while you
              register or sign in.
            </p>
          </div>

        ) : (

          /* AUTHENTICATED USER */

          <div className="mt-7 space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-medium text-slate-500">
                Signed in as
              </p>

              <p className="mt-1 break-all text-sm font-bold text-slate-900">
                {user?.email ||
                  "Authenticated user"}
              </p>

              <p className="mt-3 flex items-center gap-2 text-xs text-slate-500">
                <SafeIcon icon={FiLock} />
                Your verified email must
                match the invitation.
              </p>
            </div>

            {!verified && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-xs leading-6 text-amber-800">
                Please verify your email address
                before accepting the invitation.
                Check your email for your
                confirmation link.
              </div>
            )}

            <button
              type="button"
              disabled={
                submitting ||
                !verified ||
                switchingAccount
              }
              onClick={accept}
              className="flex w-full items-center justify-center gap-3 rounded-2xl bg-emerald-600 px-5 py-4 text-sm font-bold text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <SafeIcon
                    icon={FiLoader}
                    className="animate-spin"
                  />
                  Accepting invitation...
                </>
              ) : (
                <>
                  Accept Invitation
                  <SafeIcon
                    icon={FiArrowRight}
                  />
                </>
              )}
            </button>

            <button
              type="button"
              disabled={switchingAccount}
              onClick={switchAccount}
              className="flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 px-4 py-3 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
            >
              <SafeIcon
                icon={
                  switchingAccount
                    ? FiLoader
                    : FiLogOut
                }
                className={
                  switchingAccount
                    ? "animate-spin"
                    : ""
                }
              />

              {switchingAccount
                ? "Signing out..."
                : "Use a different account"}
            </button>

            <button
              type="button"
              onClick={() => {
                clearPendingToken();
                navigate("/", {
                  replace: true,
                });
              }}
              className="w-full px-4 py-2 text-xs font-medium text-slate-500 hover:text-slate-800"
            >
              Not now
            </button>
          </div>
        )}

        {/* FOOTER */}

        <div className="mt-7 border-t border-slate-100 pt-5 text-center">
          {!isAuthenticated && !invalid && !accepted ? (
            <p className="text-xs text-slate-500">
              Secure invitations are associated
              with a specific email address.
            </p>
          ) : (
            <Link
              to="/"
              className="text-xs font-medium text-slate-500 hover:text-emerald-700"
            >
              ProfitYo Home
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}